import React, { useEffect, useState } from 'react'
import Lifecycles from './Lifecycles'
import AiInsights from './AiInsights'

const INSIGHTS_PROC = { id: 110, name: 'kognative_INSIGHTS' }

function LoadingStatValue() {
  return <span style={s.loadingStatValue} aria-label="Loading stat" />
}

async function fetchTessBuddyCategoryId(orgData, webApiBase) {
  const catEndpoint = encodeURIComponent('ReferenceData/ListCategories/Summary?activeOnly=true')
  const catResp = await fetch(`/api/tessitura?endpoint=${catEndpoint}`, {
    method: 'GET',
    headers: {
      'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
      'x-tessitura-url': webApiBase,
      'Content-Type': 'application/json'
    }
  })
  if (!catResp.ok) throw new Error(`Categories: ${catResp.status}`)

  const catData = await catResp.json()
  const catRaw = Array.isArray(catData) ? catData : (catData?.Categories || catData?.categories || [])
  const tessBuddyCat = catRaw.find(c => {
    const name = (c?.Description ?? c?.description ?? c?.Name ?? c?.name ?? '').toLowerCase()
    return name === 'tess buddy'
  })
  const categoryId = tessBuddyCat?.Id ?? tessBuddyCat?.id ?? tessBuddyCat?.CategoryId
  if (!categoryId) throw new Error('No "Tess Buddy" list category found in Tessitura.')

  return categoryId
}

const ATTENDANCE_SEGMENTS = [
  {
    key: 'loyalists',
    label: 'Loyalists',
    count: '-',
    color: '#16a34a',
    description: 'Attended every season in the four-year window.',
    signal: 'Consistent annual attendance across all four seasons.',
    action: 'Prioritize for subscriptions, renewals, donor conversion, and early access offers.',
    kpis: [
      { label: 'PPS', value: '$94' },
      { label: 'Ticket Revenue', value: '$1.22M' },
      { label: 'Seats', value: '12,979' },
    ]
  },
  {
    key: 'supporters',
    label: 'Supporters',
    count: '-',
    color: '#1d6fdb',
    description: 'Attended three or more seasons in the four-year window.',
    signal: 'Reliable repeat attendance with one possible gap.',
    action: 'Offer curated packages and subscription upgrade messaging.',
    kpis: [
      { label: 'PPS', value: '$86' },
      { label: 'Ticket Revenue', value: '$874K' },
      { label: 'Seats', value: '10,163' },
    ]
  },
  {
    key: 'fans',
    label: 'Fans',
    count: '-',
    color: '#7c3aed',
    description: 'Attended two or more seasons in the four-year window.',
    signal: 'Returning patrons with a pattern worth strengthening.',
    action: 'Invite back with genre-aware recommendations and flexible package offers.',
    kpis: [
      { label: 'PPS', value: '$77' },
      { label: 'Ticket Revenue', value: '$691K' },
      { label: 'Seats', value: '8,974' },
    ]
  },
  {
    key: 'curious',
    label: 'Curious Engagers',
    count: '-',
    color: '#d97706',
    description: 'Attended only one season in the four-year window.',
    signal: 'Single-season engagement that may need a timely next invitation.',
    action: 'Use welcome-back messaging and low-friction second-visit offers.',
    kpis: [
      { label: 'PPS', value: '$69' },
      { label: 'Ticket Revenue', value: '$356K' },
      { label: 'Seats', value: '5,159' },
    ]
  }
]

const INTEREST_SEGMENTS = [
  {
    key: 'theater',
    label: 'Theater',
    count: '-',
    color: '#1d6fdb',
    description: 'Patrons whose ranked interest points to theater programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted theater interest list.'
  },
  {
    key: 'music',
    label: 'Music',
    count: '-',
    color: '#0891b2',
    description: 'Patrons whose ranked interest points to music programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted music interest list.'
  },
  {
    key: 'dance',
    label: 'Dance',
    count: '-',
    color: '#7c3aed',
    description: 'Patrons whose ranked interest points to dance programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted dance interest list.'
  },
  {
    key: 'cistern',
    label: 'Cistern',
    count: '-',
    color: '#b45309',
    description: 'Patrons whose ranked interest points to Cistern programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted Cistern interest list.'
  },
  {
    key: 'opera',
    label: 'Opera',
    count: '-',
    color: '#dc2626',
    description: 'Patrons whose ranked interest points to opera programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted opera interest list.'
  },
  {
    key: 'jazz',
    label: 'Jazz',
    count: '-',
    color: '#0f766e',
    description: 'Patrons whose ranked interest points to jazz programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted jazz interest list.'
  },
  {
    key: 'chamber',
    label: 'Chamber',
    count: '-',
    color: '#4f46e5',
    description: 'Patrons whose ranked interest points to chamber programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted chamber interest list.'
  },
  {
    key: 'orch',
    label: 'Orch',
    count: '-',
    color: '#9333ea',
    description: 'Patrons whose ranked interest points to orchestral programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted orchestral interest list.'
  },
  {
    key: 'choral',
    label: 'Choral',
    count: '-',
    color: '#64748b',
    description: 'Patrons whose ranked interest points to choral programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted choral interest list.'
  },
  {
    key: 'family',
    label: 'Family',
    count: '-',
    color: '#d97706',
    description: 'Patrons whose ranked interest points to family programming.',
    signal: 'Ranked by four years of purchase history, click data, and marketing suppressions.',
    action: 'Build a targeted family interest list.'
  }
]

const GEOGRAPHIC_SEGMENTS = [
  {
    key: 'charleston',
    label: 'Charleston Metro',
    count: '-',
    color: '#1d6fdb',
    description: 'Local patrons in the core Charleston-area market.',
    signal: 'Primary postal code starts with 294.',
    action: 'Build a local-market audience list.',
    kpis: [
      { label: 'PPS', value: '$82' },
      { label: 'Ticket Revenue', value: '$1.05M' },
      { label: 'Seats', value: '12,805' },
    ]
  },
  {
    key: 'south_carolina',
    label: 'South Carolina',
    count: '-',
    color: '#16a34a',
    description: 'In-state patrons outside the core local market.',
    signal: 'Primary state is SC and postal code does not start with 294.',
    action: 'Build an in-state audience list.',
    kpis: [
      { label: 'PPS', value: '$78' },
      { label: 'Ticket Revenue', value: '$612K' },
      { label: 'Seats', value: '7,846' },
    ]
  },
  {
    key: 'regional',
    label: 'Regional Drive Market',
    count: '-',
    color: '#7c3aed',
    description: 'Nearby out-of-state patrons within practical travel distance.',
    signal: 'Primary state is NC, GA, FL, VA, or TN.',
    action: 'Build a regional travel-market list.',
    kpis: [
      { label: 'PPS', value: '$91' },
      { label: 'Ticket Revenue', value: '$438K' },
      { label: 'Seats', value: '4,813' },
    ]
  },
  {
    key: 'national',
    label: 'National Audience',
    count: '-',
    color: '#d97706',
    description: 'Out-of-region patrons who may need destination-oriented messaging.',
    signal: 'Primary address falls outside the Charleston, South Carolina, and regional buckets.',
    action: 'Build a national audience list.',
    kpis: [
      { label: 'PPS', value: '$104' },
      { label: 'Ticket Revenue', value: '$291K' },
      { label: 'Seats', value: '2,798' },
    ]
  }
]

function InsightsHub({ onUse, orgData }) {
  const [activeTab, setActiveTab] = useState('lifecycles')

  return (
    <div>
      <style>{`
        @keyframes tessBuddyStatPulse {
          0% { opacity: 0.45; transform: scaleX(0.92); }
          50% { opacity: 1; transform: scaleX(1); }
          100% { opacity: 0.45; transform: scaleX(0.92); }
        }
      `}</style>
      <div style={s.header}>
        <h1 style={s.title}>Insights</h1>
        <p style={s.subtitle}>
          Sales pacing, patron lifecycles, and AI-powered analysis — all in one place.
        </p>
      </div>

      <div style={s.tabBar}>
        <button style={activeTab === 'lifecycles' ? s.tabActive : s.tab} onClick={() => setActiveTab('lifecycles')}>Lifecycles</button>
        <button style={activeTab === 'behavior'   ? s.tabActive : s.tab} onClick={() => setActiveTab('behavior')}>Customer Behavior</button>
        <button style={activeTab === 'ai'         ? s.tabActive : s.tab} onClick={() => setActiveTab('ai')}>AI Chat</button>
      </div>

      <div style={activeTab === 'lifecycles' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <Lifecycles embedded orgData={orgData} />
      </div>
      <div style={activeTab === 'behavior' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <BehaviorTab orgData={orgData} />
      </div>
      {activeTab === 'ai' && <AiInsights embedded onUse={onUse} orgData={orgData} />}
    </div>
  )
}

export function BehaviorTab({ orgData }) {
  const [activeSubTab, setActiveSubTab] = useState('attendance')

  return (
    <div>
      <div style={s.subTabBar}>
        <button
          style={activeSubTab === 'attendance' ? s.tabActive : s.tab}
          onClick={() => setActiveSubTab('attendance')}
        >
          Attendance Frequency
        </button>
        <button
          style={activeSubTab === 'interest' ? s.tabActive : s.tab}
          onClick={() => setActiveSubTab('interest')}
        >
          Interest Modeling
        </button>
        <button
          style={activeSubTab === 'geographic' ? s.tabActive : s.tab}
          onClick={() => setActiveSubTab('geographic')}
        >
          Geographic
        </button>
      </div>

      <div style={activeSubTab === 'attendance' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <AttendanceFrequencyTab orgData={orgData} />
      </div>
      <div style={activeSubTab === 'interest' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <InterestModelingTab orgData={orgData} />
      </div>
      <div style={activeSubTab === 'geographic' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <GeographicTab orgData={orgData} />
      </div>
    </div>
  )
}

function GeographicTab({ orgData }) {
  const [geographicStats, setGeographicStats] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  const parseRows = (text) => {
    try {
      const data = JSON.parse(text)
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  }

  const encodeSql = (sql) => {
    return btoa(unescape(encodeURIComponent(sql)))
  }

  const procHeaders = {
    'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
    'x-tessitura-url': orgData?.organizations?.tessitura_base_url,
    'Content-Type': 'application/json'
  }

  const listApiBase = () => {
    return (orgData?.organizations?.tessitura_base_url || '')
      .replace(/\/TessituraService\/?$/i, '/Tessitura/api/')
  }

  const callInsightsProc = async (parameterValues) => {
    const response = await fetch('/api/tessitura?endpoint=Custom/Execute', {
      method: 'POST',
      headers: procHeaders,
      body: JSON.stringify({
        ProcedureId: INSIGHTS_PROC.id,
        ProcedureName: INSIGHTS_PROC.name,
        ParameterValues: parameterValues
      })
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const fetchGeographicStats = async () => {
    setLoading(true)
    setMessage(null)
    try {
      const result = await callInsightsProc([
        { Name: '@mode', Value: 'GEOGRAPHIC' }
      ])

      if (!result.ok) {
        setMessage({ type: 'error', text: 'Could not load geographic counts.' })
        setLoading(false)
        return
      }

      setGeographicStats(parseRows(result.text))
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load geographic counts: ' + err.message })
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchGeographicStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getStatForBucket = (key) => {
    return geographicStats.find(row => String(row.bucket_key || '').toLowerCase() === key)
  }

  const cards = GEOGRAPHIC_SEGMENTS.map(card => ({
    ...card,
    count: getStatForBucket(card.key)?.customer_count?.toLocaleString?.() ?? getStatForBucket(card.key)?.customer_count ?? '-',
    loading
  }))

  const handleMakeGeographicList = async (card) => {
    setMessage(null)
    try {
      const sqlResult = await callInsightsProc([
        { Name: '@mode', Value: 'GEOGRAPHIC_LIST_SQL' },
        { Name: '@bucket_key', Value: card.key }
      ])

      if (!sqlResult.ok) {
        return { ok: false, text: 'Could not build the geographic list SQL.' }
      }

      const sqlRow = parseRows(sqlResult.text)[0] || {}
      if (!sqlRow.list_sql) {
        return { ok: false, text: 'The geographic list SQL did not come back from Tessitura.' }
      }

      const categoryId = await fetchTessBuddyCategoryId(orgData, listApiBase())

      const createResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('Custom/Execute')}`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
          'x-tessitura-url': listApiBase(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureName: 'kognative_CREATE_TESSBUDDY_LIST',
          ParameterValues: [
            { Name: '@description', Value: (sqlRow.description || card.label).slice(0, 30) },
            { Name: '@list_sql_b64', Value: encodeSql(sqlRow.list_sql) },
            { Name: '@category_id', Value: String(categoryId) }
          ]
        })
      })

      const createText = await createResp.text()
      if (!createResp.ok) {
        return { ok: false, text: createText.slice(0, 240) || `Tessitura returned ${createResp.status}.` }
      }

      return { ok: true, text: `${card.label} geographic list created.` }
    } catch (err) {
      return { ok: false, text: err.message }
    }
  }

  return (
    <div>
      <BehaviorCardGrid
        title="Geographic"
        description="Active patrons grouped by active primary-address market area."
        cards={cards}
        countLabel="people"
        onMakeList={handleMakeGeographicList}
      />
      {message && (
        <div style={{ ...s.messageBox, color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>
          {message.text}
        </div>
      )}
    </div>
  )
}

function AttendanceFrequencyTab({ orgData }) {
  const [attendanceStats, setAttendanceStats] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  const parseRows = (text) => {
    try {
      const data = JSON.parse(text)
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  }

  const encodeSql = (sql) => {
    return btoa(unescape(encodeURIComponent(sql)))
  }

  const procHeaders = {
    'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
    'x-tessitura-url': orgData?.organizations?.tessitura_base_url,
    'Content-Type': 'application/json'
  }

  const listApiBase = () => {
    return (orgData?.organizations?.tessitura_base_url || '')
      .replace(/\/TessituraService\/?$/i, '/Tessitura/api/')
  }

  const callInsightsProc = async (parameterValues) => {
    const response = await fetch('/api/tessitura?endpoint=Custom/Execute', {
      method: 'POST',
      headers: procHeaders,
      body: JSON.stringify({
        ProcedureId: INSIGHTS_PROC.id,
        ProcedureName: INSIGHTS_PROC.name,
        ParameterValues: parameterValues
      })
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const fetchAttendanceStats = async () => {
    setLoading(true)
    setMessage(null)
    try {
      const result = await callInsightsProc([
        { Name: '@mode', Value: 'ATTENDANCE_FREQUENCY' }
      ])

      if (!result.ok) {
        setMessage({ type: 'error', text: 'Could not load attendance frequency counts.' })
        setLoading(false)
        return
      }

      setAttendanceStats(parseRows(result.text))
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load attendance frequency counts: ' + err.message })
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchAttendanceStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getStatForBucket = (key) => {
    return attendanceStats.find(row => String(row.bucket_key || '').toLowerCase() === key)
  }

  const firstRow = attendanceStats[0] || {}
  const windowText = firstRow.start_fyear && firstRow.end_fyear
    ? `${firstRow.start_fyear}-${firstRow.end_fyear}`
    : 'current four-year window'

  const cards = ATTENDANCE_SEGMENTS.map(card => ({
    ...card,
    count: getStatForBucket(card.key)?.customer_count?.toLocaleString?.() ?? getStatForBucket(card.key)?.customer_count ?? '-',
    loading,
    signal: `${card.signal} Window: ${windowText}.`
  }))

  const handleMakeAttendanceList = async (card) => {
    setMessage(null)
    try {
      const sqlResult = await callInsightsProc([
        { Name: '@mode', Value: 'ATTENDANCE_FREQUENCY_LIST_SQL' },
        { Name: '@bucket_key', Value: card.key }
      ])

      if (!sqlResult.ok) {
        return { ok: false, text: 'Could not build the attendance frequency list SQL.' }
      }

      const sqlRow = parseRows(sqlResult.text)[0] || {}
      if (!sqlRow.list_sql) {
        return { ok: false, text: 'The attendance frequency list SQL did not come back from Tessitura.' }
      }

      const categoryId = await fetchTessBuddyCategoryId(orgData, listApiBase())

      const createResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('Custom/Execute')}`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
          'x-tessitura-url': listApiBase(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureName: 'kognative_CREATE_TESSBUDDY_LIST',
          ParameterValues: [
            { Name: '@description', Value: (sqlRow.description || card.label).slice(0, 30) },
            { Name: '@list_sql_b64', Value: encodeSql(sqlRow.list_sql) },
            { Name: '@category_id', Value: String(categoryId) }
          ]
        })
      })

      const createText = await createResp.text()
      if (!createResp.ok) {
        return { ok: false, text: createText.slice(0, 240) || `Tessitura returned ${createResp.status}.` }
      }

      return { ok: true, text: `${card.label} attendance frequency list created.` }
    } catch (err) {
      return { ok: false, text: err.message }
    }
  }

  return (
    <div>
      <BehaviorCardGrid
        title="Four-Year Attendance Frequency"
        description={`Patrons grouped by distinct seasons attended in ${windowText}.`}
        cards={cards}
        onMakeList={handleMakeAttendanceList}
      />
      {message && (
        <div style={{ ...s.messageBox, color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>
          {message.text}
        </div>
      )}
    </div>
  )
}

function InterestModelingTab({ orgData }) {
  const [favoriteRank, setFavoriteRank] = useState(1)
  const [interestStatsByRank, setInterestStatsByRank] = useState({ 1: [], 2: [] })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const rankLabel = favoriteRank === 1 ? 'Top favorite genre' : 'Second favorite genre'
  const rankDescription = favoriteRank === 1
    ? 'People whose strongest ranked interest is this genre.'
    : 'People whose second strongest ranked interest is this genre.'

  const parseRows = (text) => {
    try {
      const data = JSON.parse(text)
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  }

  const encodeSql = (sql) => {
    return btoa(unescape(encodeURIComponent(sql)))
  }

  const procHeaders = {
    'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
    'x-tessitura-url': orgData?.organizations?.tessitura_base_url,
    'Content-Type': 'application/json'
  }

  const listApiBase = () => {
    return (orgData?.organizations?.tessitura_base_url || '')
      .replace(/\/TessituraService\/?$/i, '/Tessitura/api/')
  }

  const callInsightsProc = async (parameterValues) => {
    const response = await fetch('/api/tessitura?endpoint=Custom/Execute', {
      method: 'POST',
      headers: procHeaders,
      body: JSON.stringify({
        ProcedureId: INSIGHTS_PROC.id,
        ProcedureName: INSIGHTS_PROC.name,
        ParameterValues: parameterValues
      })
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const fetchInterestStats = async () => {
    setLoading(true)
    setMessage(null)
    try {
      const results = await Promise.all([1, 2].map(async rank => {
        const result = await callInsightsProc([
          { Name: '@mode', Value: 'INTEREST_MODELING' },
          { Name: '@rank_interest', Value: String(rank) }
        ])
        return { rank, result }
      }))

      const failed = results.find(({ result }) => !result.ok)
      if (failed) {
        setMessage({ type: 'error', text: 'Could not load interest modeling counts.' })
        setLoading(false)
        return
      }

      setInterestStatsByRank(results.reduce((nextStats, { rank, result }) => ({
        ...nextStats,
        [rank]: parseRows(result.text)
      }), { 1: [], 2: [] }))
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load interest modeling counts: ' + err.message })
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchInterestStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getStatForGenre = (label) => {
    const interestStats = interestStatsByRank[favoriteRank] || []
    return interestStats.find(row => String(row.segment_label || '').toLowerCase() === label.toLowerCase())
  }

  const cards = INTEREST_SEGMENTS.map(card => ({
    ...card,
    count: getStatForGenre(card.label)?.customer_count?.toLocaleString?.() ?? getStatForGenre(card.label)?.customer_count ?? '-',
    loading,
    signal: rankDescription,
    action: `${card.action} The list will use ${favoriteRank === 1 ? 'top favorite' : 'second favorite'} genre ranking.`
  }))

  const handleMakeInterestList = async (card) => {
    setMessage(null)
    try {
      const sqlResult = await callInsightsProc([
        { Name: '@mode', Value: 'INTEREST_MODELING_LIST_SQL' },
        { Name: '@interest_name', Value: card.label },
        { Name: '@rank_interest', Value: String(favoriteRank) }
      ])

      if (!sqlResult.ok) {
        return { ok: false, text: 'Could not build the interest list SQL.' }
      }

      const sqlRow = parseRows(sqlResult.text)[0] || {}
      if (!sqlRow.list_sql) {
        return { ok: false, text: 'The interest list SQL did not come back from Tessitura.' }
      }

      const categoryId = await fetchTessBuddyCategoryId(orgData, listApiBase())

      const createResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('Custom/Execute')}`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
          'x-tessitura-url': listApiBase(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureName: 'kognative_CREATE_TESSBUDDY_LIST',
          ParameterValues: [
            { Name: '@description', Value: (sqlRow.description || `${card.label} Interest`).slice(0, 30) },
            { Name: '@list_sql_b64', Value: encodeSql(sqlRow.list_sql) },
            { Name: '@category_id', Value: String(categoryId) }
          ]
        })
      })

      const createText = await createResp.text()
      if (!createResp.ok) {
        return { ok: false, text: createText.slice(0, 240) || `Tessitura returned ${createResp.status}.` }
      }

      return { ok: true, text: `${card.label} ${favoriteRank === 1 ? 'top' : 'second'} favorite genre list created.` }
    } catch (err) {
      return { ok: false, text: err.message }
    }
  }

  return (
    <div>
      <div style={s.panel}>
        <div style={s.panelHeaderRow}>
          <div>
            <p style={s.panelTitle}>Interest Modeling</p>
            <p style={s.panelText}>
              Counts patrons by ranked genre interest, using purchase history, click data, and current marketing suppressions.
            </p>
          </div>
          <div style={s.rankToggle} role="group" aria-label="Favorite genre rank">
            <button
              type="button"
              style={favoriteRank === 1 ? { ...s.rankToggleButton, ...s.rankToggleButtonActive } : s.rankToggleButton}
              onClick={() => setFavoriteRank(1)}
            >
              Top Favorite
            </button>
            <button
              type="button"
              style={favoriteRank === 2 ? { ...s.rankToggleButton, ...s.rankToggleButtonActive } : s.rankToggleButton}
              onClick={() => setFavoriteRank(2)}
            >
              Second Favorite
            </button>
          </div>
        </div>
      </div>

      <BehaviorCardGrid
        title={rankLabel}
        description={rankDescription}
        cards={cards}
        countLabel="people"
        listContext={{ favoriteRank }}
        onMakeList={handleMakeInterestList}
        showPanel={false}
      />
      {message && (
        <div style={{ ...s.messageBox, color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>
          {message.text}
        </div>
      )}
    </div>
  )
}

function BehaviorCardGrid({ title, description, cards, countLabel = 'patrons', listContext = {}, onMakeList = null, showPanel = true }) {
  const [buildingList, setBuildingList] = useState(null)
  const [builtLists, setBuiltLists] = useState({})
  const [cardMessages, setCardMessages] = useState({})

  const handleMakeList = async (card) => {
    setCardMessages(prev => ({ ...prev, [card.key]: null }))
    if (!onMakeList) {
      console.log('Make behavior list: not yet implemented', { key: card.key, ...listContext })
      return
    }

    setBuildingList(card.key)
    const result = await onMakeList(card)
    setBuildingList(null)

    if (result?.ok) {
      setBuiltLists(prev => ({ ...prev, [card.key]: true }))
    }
    setCardMessages(prev => ({ ...prev, [card.key]: result?.text || null }))
  }

  return (
    <div>
      {showPanel && (
        <div style={s.panel}>
          <p style={s.panelTitle}>{title}</p>
          <p style={s.panelText}>{description}</p>
        </div>
      )}

      <div style={s.cardGrid}>
        {cards.map(card => {
          const isBuilding = buildingList === card.key
          const isBuilt = !!builtLists[card.key]

          return (
            <div key={card.key} style={{ ...s.behaviorCard, borderColor: `${card.color}30`, boxShadow: `0 2px 12px ${card.color}12` }}>
              <div style={s.cardHeader}>
                <p style={{ ...s.cardTitle, color: card.color }}>{card.label}</p>
                <div style={s.cardCountBlock}>
                  <p style={s.cardCount}>{card.loading ? <LoadingStatValue /> : card.count}</p>
                  <p style={s.cardCountLabel}>{card.countLabel || countLabel}</p>
                </div>
              </div>

              <p style={s.cardDescription}>{card.description}</p>

              <div style={s.detailBlock}>
                <p style={s.detailLabel}>Signal</p>
                <p style={s.detailText}>{card.signal}</p>
              </div>
              {card.kpis?.length ? (
                <div style={s.kpiGrid}>
                  {card.kpis.map(kpi => (
                    <div key={kpi.label} style={s.kpiCard}>
                      <p style={s.detailLabel}>{kpi.label}</p>
                      <p style={s.kpiValue}>{kpi.value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={s.detailBlock}>
                  <p style={s.detailLabel}>Recommended Action</p>
                  <p style={s.detailText}>{card.action}</p>
                </div>
              )}

              <button
                onClick={() => handleMakeList(card)}
                disabled={isBuilding || isBuilt}
                style={{
                  ...s.segmentButton,
                  background: isBuilt ? '#f0fff4' : isBuilding ? '#d1d9e6' : 'linear-gradient(135deg, #1d6fdb, #38bdf8)',
                  color: isBuilt ? '#16a34a' : 'white',
                  cursor: isBuilding || isBuilt ? 'default' : 'pointer',
                  border: isBuilt ? '1px solid #c6f6d5' : 'none'
                }}
              >
                {isBuilt ? 'Segment created' : isBuilding ? 'Building...' : '+ Add to Segment'}
              </button>
              {cardMessages[card.key] && (
                <p style={{ ...s.cardMessage, color: isBuilt ? '#16a34a' : '#dc2626' }}>
                  {cardMessages[card.key]}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const s = {
  header:   { marginBottom: '24px' },
  title:    { fontSize: '24px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' },
  subtitle: { fontSize: '13px', color: '#6b7280', fontFamily: "'Inter', sans-serif", lineHeight: '1.6', maxWidth: '580px' },
  tabBar:   { display: 'flex', gap: '4px', borderBottom: '1px solid rgba(29,111,219,0.15)', marginBottom: '28px' },
  subTabBar:{ display: 'flex', gap: '4px', borderBottom: '1px solid rgba(29,111,219,0.15)', marginBottom: '24px' },
  visibleTabPanel: { display: 'block' },
  hiddenTabPanel: { display: 'none' },
  tab:      { padding: '10px 18px', backgroundColor: 'transparent', color: '#4b5563', border: 'none', borderBottom: '2px solid transparent', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  tabActive:{ padding: '10px 18px', backgroundColor: 'transparent', color: '#0c1a33', border: 'none', borderBottom: '2px solid #1d6fdb', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  panel:    { backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', maxWidth: '960px', marginBottom: '20px' },
  panelHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '18px', flexWrap: 'wrap' },
  panelTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', margin: '0 0 8px 0', fontFamily: "'Inter', sans-serif" },
  panelText: { fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0, fontFamily: "'Inter', sans-serif" },
  rankToggle: { display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '3px', backgroundColor: '#f0f7ff', border: '1px solid rgba(29,111,219,0.14)', borderRadius: '10px', flexShrink: 0 },
  rankToggleButton: { border: 'none', backgroundColor: 'transparent', color: '#4b5563', padding: '8px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  rankToggleButtonActive: { backgroundColor: 'white', color: '#0c1a33', boxShadow: '0 1px 6px rgba(29,111,219,0.12)' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', maxWidth: '960px' },
  behaviorCard: { background: 'white', borderRadius: '12px', border: '1px solid rgba(29,111,219,0.1)', overflow: 'hidden', padding: '16px 18px' },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', margin: 0, fontFamily: "'Space Grotesk', sans-serif" },
  cardCountBlock: { textAlign: 'right', flexShrink: 0 },
  cardCount: { fontSize: '22px', fontWeight: '800', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 },
  loadingStatValue: { display: 'inline-block', width: '54px', height: '22px', borderRadius: '7px', background: 'linear-gradient(135deg, rgba(29,111,219,0.12), rgba(56,189,248,0.28))', transformOrigin: 'right center', animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  cardCountLabel: { fontSize: '10px', color: '#9ca3af', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" },
  cardDescription: { fontSize: '12px', color: '#4b5563', lineHeight: '1.55', margin: '0 0 14px', fontFamily: "'Inter', sans-serif" },
  detailBlock: { marginBottom: '10px' },
  detailLabel: { fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 3px', fontFamily: "'Inter', sans-serif" },
  detailText: { fontSize: '12px', color: '#4b5563', lineHeight: '1.45', margin: 0, fontFamily: "'Inter', sans-serif" },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginBottom: '12px' },
  kpiCard: { padding: '10px 12px', background: '#f8fafd', border: '1px solid rgba(29,111,219,0.08)', borderRadius: '8px', minWidth: 0 },
  kpiValue: { fontSize: '15px', fontWeight: '800', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif" },
  segmentButton: { padding: '7px 16px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif" },
  cardMessage: { margin: '8px 0 0', fontSize: '11px', fontWeight: '600', lineHeight: '1.35', fontFamily: "'Inter', sans-serif" },
  messageBox: { marginTop: '14px', padding: '10px 14px', backgroundColor: '#f8fafd', border: '1px solid rgba(29,111,219,0.12)', borderRadius: '8px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif" },
}

export default InsightsHub
